import { create } from 'zustand';

export type HandId = 'left' | 'right';
export type CameraStatus =
  | 'idle'
  | 'requesting'
  | 'streaming'
  | 'denied'
  | 'error';

export interface Vec2 {
  x: number;
  y: number;
  /** Relative depth from MediaPipe (wrist = 0; smaller = closer to camera).
   *  Optional because older fixtures/tests use plain 2D points. */
  z?: number;
}

/** 21 landmarks as emitted by MediaPipe HandLandmarker. */
export type Landmarks = readonly Vec2[];

export interface HandSnapshot {
  id: HandId;
  present: boolean;
  landmarks: Landmarks | null;
  /** Centroid of the palm (avg of wrist + MCPs), normalized 0..1 */
  palm: Vec2;
  /** Palm width in normalized units (for scaling thresholds). */
  palmWidth: number;
  /** Detection confidence 0..1 */
  confidence: number;
  /** Timestamp of the latest detection (performance.now()) */
  updatedAt: number;
}

interface HandState {
  cameraStatus: CameraStatus;
  cameraError: string | null;
  left: HandSnapshot;
  right: HandSnapshot;
  detecting: boolean;

  setCameraStatus: (s: CameraStatus, error?: string | null) => void;
  setDetecting: (v: boolean) => void;
  updateHand: (hand: HandSnapshot) => void;
  resetHand: (id: HandId) => void;
}

const emptyHand = (id: HandId): HandSnapshot => ({
  id,
  present: false,
  landmarks: null,
  palm: { x: 0.5, y: 0.5 },
  palmWidth: 0,
  confidence: 0,
  updatedAt: 0,
});

export const useHandStore = create<HandState>((set) => ({
  cameraStatus: 'idle',
  cameraError: null,
  left: emptyHand('left'),
  right: emptyHand('right'),
  detecting: false,

  setCameraStatus: (cameraStatus, cameraError = null) =>
    set({ cameraStatus, cameraError }),
  setDetecting: (detecting) => set({ detecting }),
  updateHand: (hand) =>
    set(hand.id === 'left' ? { left: hand } : { right: hand }),
  resetHand: (id) =>
    set(id === 'left' ? { left: emptyHand('left') } : { right: emptyHand('right') }),
}));
